/**
 * User Context
 *
 * Provides app-wide access to authentication state.
 * For tenant information, use useTenant() from TenantProvider.
 */

import { createContext, useCallback, useContext, useEffect, ReactNode } from "react";
import { useAuthStore } from "@src/features/auth/auth-store";
import type { User, Auth0TokenResponse, AuthSessionTokenResponse } from "@src/features/auth/types";
import LegacyKitModule from "../../../modules/legacy-kit";
import { clearLastUsedTenant } from "./last-used-tenant-store";

interface UserContextValue {
  // Auth state
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isAuthenticating: boolean;
  authError: string | null;

  // Auth actions
  setAuth: (tokens: Auth0TokenResponse, user?: User | null) => Promise<void>;
  clearAuth: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshToken: () => Promise<boolean>;
  initializeAuth: () => Promise<void>;
  setAuthenticating: (isAuthenticating: boolean) => void;
  setAuthError: (error: string | null) => void;
  handleTokenExchange: (tokenResponse: AuthSessionTokenResponse) => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  // Subscribe to auth store
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticating = useAuthStore((state) => state.isAuthenticating);
  const authError = useAuthStore((state) => state.authError);
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuthStore = useAuthStore((state) => state.clearAuth);
  const getAccessToken = useAuthStore((state) => state.getAccessToken);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const initializeAuth = useAuthStore((state) => state.initialize);
  const setAuthenticating = useAuthStore((state) => state.setAuthenticating);
  const setAuthError = useAuthStore((state) => state.setAuthError);
  const handleTokenExchange = useAuthStore((state) => state.handleTokenExchange);

  // Clear auth and tenant preference
  const clearAuth = useCallback(async () => {
    await clearLastUsedTenant();
    await clearAuthStore();
  }, [clearAuthStore]);

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Listen for logout events from the legacy app
  useEffect(() => {
    // @ts-expect-error baseline 2026-02-20 - addListener exists at runtime but not in TS types
    const subscription = LegacyKitModule.addListener("onUserLoggedOut", async (_event: unknown) => {
      console.log("[UserContext] User logged out from legacy app, clearing React Native storage");

      // Clear all React Native storage (auth tokens, user data, etc.)
      try {
        await clearAuth();
      } catch (error) {
        console.error("[UserContext] Failed to clear auth storage:", error);
      }
    }) as { remove: () => void };

    return () => {
      subscription.remove();
    };
  }, [clearAuth]);

  const contextValue: UserContextValue = {
    // Auth state
    user,
    accessToken,
    isAuthenticated,
    isInitialized,
    isAuthenticating,
    authError,

    // Auth actions
    setAuth,
    clearAuth,
    getAccessToken,
    refreshToken,
    initializeAuth,
    setAuthenticating,
    setAuthError,
    handleTokenExchange,
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

/**
 * Hook to access user context (auth state only)
 * For tenant state, use useTenant() from TenantProvider
 * @throws Error if used outside of UserProvider
 */
export function useUser() {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
}

/**
 * Hook to get auth state
 * Returns the auth-specific parts of the user context
 */
export function useAuth() {
  const context = useUser();
  return {
    user: context.user,
    accessToken: context.accessToken,
    isAuthenticated: context.isAuthenticated,
    isInitialized: context.isInitialized,
    isAuthenticating: context.isAuthenticating,
    authError: context.authError,
    setAuth: context.setAuth,
    clearAuth: context.clearAuth,
    getAccessToken: context.getAccessToken,
    refreshToken: context.refreshToken,
    initialize: context.initializeAuth,
    setAuthenticating: context.setAuthenticating,
    setAuthError: context.setAuthError,
    handleTokenExchange: context.handleTokenExchange,
  };
}

/**
 * Whether the current user holds the `a3s:support` role. Canonical source for
 * support-only gating (support tools, impersonation, support-only menu items).
 *
 * Returns a plain boolean and therefore collapses "auth not settled yet" into
 * `false`. For gates that render a hard "access restricted" screen on a cold
 * deep-link, prefer {@link useSupportUserStatus}, which distinguishes the
 * still-loading state so the screen doesn't bail out before roles are known.
 */
export function useIsSupportUser(): boolean {
  const { user } = useAuth();
  return user?.roles?.includes("a3s:support") ?? false;
}

/**
 * Tri-state support-user gate for screens that would otherwise show a hard
 * "access restricted" message on a direct URL / cold load.
 *
 * - `"pending"`  — auth hasn't settled yet (initialising or authenticating), so
 *   roles are not yet known. Callers should show a loading state, NOT deny.
 * - `"granted"`  — auth settled and the `a3s:support` role is present.
 * - `"denied"`   — auth settled and the role is absent.
 *
 * Why this exists: `useIsSupportUser` returns `false` while roles are still
 * loading, which made support-only pages permanently render their restricted
 * screen when opened via a direct link (the guard has no loading branch, so it
 * doesn't recover once roles arrive). Gating on the settled state fixes that.
 */
export type SupportUserStatus = "pending" | "granted" | "denied";

export function useSupportUserStatus(): SupportUserStatus {
  const { user, isInitialized, isAuthenticating } = useAuth();
  // Roles are only trustworthy once auth has finished settling. Until then,
  // report "pending" so callers wait rather than deny.
  if (!isInitialized || isAuthenticating) return "pending";
  return user?.roles?.includes("a3s:support") ? "granted" : "denied";
}
